import React, { useState } from "react";
import Icon from "../../../components/AppIcon";
import Button from "../../../components/ui/Button";
import type { Payment } from "../../../store/api/paymentsApi";
import { formatDateTime } from "../../../utils/formatDate";

interface PaymentTableProps {
	payments: Payment[];
	selectedPayments: string[];
	onPaymentSelect: (paymentId: string, selected: boolean) => void;
	onSelectAll: (selected: boolean) => void;
	onPaymentClick: (payment: Payment) => void;
	onRecordPayment: (paymentId: string) => void;
	onDeletePayment: (paymentId: string) => void;
}

const PaymentTable: React.FC<PaymentTableProps> = ({
	payments,
	selectedPayments,
	onPaymentSelect,
	onSelectAll,
	onPaymentClick,
	onRecordPayment,
	onDeletePayment,
}) => {
	const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
	const [sortConfig, setSortConfig] = useState<{
		key: string;
		direction: "asc" | "desc";
	}>({ key: "due_date", direction: "desc" });

	const toggleRowExpansion = (paymentId: string) => {
		const newExpanded = new Set(expandedRows);
		if (newExpanded.has(paymentId)) {
			newExpanded.delete(paymentId);
		} else {
			newExpanded.add(paymentId);
		}
		setExpandedRows(newExpanded);
	};

	const handleSort = (key: string) => {
		setSortConfig((prev) => ({
			key,
			direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
		}));
	};

	const sortedPayments = [...payments].sort((a, b) => {
		const aValue: any = a[sortConfig.key as keyof Payment];
		const bValue: any = b[sortConfig.key as keyof Payment];

		if (sortConfig.direction === "asc") {
			return aValue > bValue ? 1 : -1;
		} else {
			return aValue < bValue ? 1 : -1;
		}
	});

	const getStatusBadge = (status: string, dueDate?: string) => {
		const baseClasses = "px-2 py-1 text-xs font-medium rounded-full capitalize";
		const isOverdue =
			status === "pending" && dueDate && new Date(dueDate) < new Date();

		if (isOverdue || status === "overdue") {
			return `${baseClasses} bg-error/10 text-error`;
		}

		switch (status) {
			case "paid":
				return `${baseClasses} bg-success/10 text-success`;
			case "pending":
				return `${baseClasses} bg-warning/10 text-warning`;
			case "cancelled":
			case "refunded":
				return `${baseClasses} bg-muted text-muted-foreground`;
			default:
				return `${baseClasses} bg-muted text-muted-foreground`;
		}
	};

	const formatCurrency = (amount: number) => {
		return new Intl.NumberFormat("en-NG", {
			style: "currency",
			currency: "NGN",
		}).format(amount);
	};

	// Using centralized formatDateTimeTime utility

	const isOverdue = (payment: Payment) => {
		return (
			payment.status === "pending" &&
			payment.due_date &&
			new Date(payment.due_date) < new Date()
		);
	};

	return (
		<div className="bg-card border border-border rounded-lg card-shadow overflow-hidden">
			{/* Table Header */}
			<div className="p-4 border-b border-border">
				<div className="flex items-center justify-between">
					<h3 className="font-semibold text-foreground">Payment Records</h3>
					<p className="text-sm text-muted-foreground">
						{payments.length} payment{payments.length !== 1 ? "s" : ""}
					</p>
				</div>
			</div>

			{/* Table with horizontal scroll on mobile */}
			<div className="overflow-x-auto -mx-6 sm:mx-0">
				<div className="inline-block min-w-full align-middle sm:px-0">
					<table className="min-w-full divide-y divide-border">
					<thead className="bg-muted/50 border-b border-border">
						<tr>
							<th className="w-12 p-4">
								<input
									type="checkbox"
									checked={
										payments.length > 0 &&
										selectedPayments.length === payments.length
									}
									onChange={(e) => onSelectAll(e.target.checked)}
									className="rounded border-border"
								/>
							</th>
							<th className="text-left p-4 font-medium text-foreground">
								<button
									onClick={() => handleSort("tenant_name")}
									className="flex items-center space-x-1 hover:text-primary"
								>
									<span>Tenant</span>
									<Icon
										name={
											sortConfig.key === "tenant_name" &&
											sortConfig.direction === "desc"
												? "ChevronDown"
												: "ChevronUp"
										}
										size={16}
									/>
								</button>
							</th>
							<th className="text-left p-4 font-medium text-foreground">
								Property
							</th>
							<th className="text-left p-4 font-medium text-foreground">
								<button
									onClick={() => handleSort("amount")}
									className="flex items-center space-x-1 hover:text-primary"
								>
									<span>Amount</span>
									<Icon
										name={
											sortConfig.key === "amount" &&
											sortConfig.direction === "desc"
												? "ChevronDown"
												: "ChevronUp"
										}
										size={16}
									/>
								</button>
							</th>
							<th className="text-left p-4 font-medium text-foreground">
								<button
									onClick={() => handleSort("due_date")}
									className="flex items-center space-x-1 hover:text-primary"
								>
									<span>Due Date</span>
									<Icon
										name={
											sortConfig.key === "due_date" &&
											sortConfig.direction === "desc"
												? "ChevronDown"
												: "ChevronUp"
										}
										size={16}
									/>
								</button>
							</th>
							<th className="text-left p-4 font-medium text-foreground">
								Status
							</th>
							<th className="text-right p-4 font-medium text-foreground">
								Actions
							</th>
						</tr>
					</thead>
					<tbody>
						{sortedPayments.map((payment) => (
							<React.Fragment key={payment.id}>
								<tr
									className="border-b border-border hover:bg-muted/30 cursor-pointer smooth-transition"
									onClick={() => onPaymentClick(payment)}
								>
									<td className="p-4" onClick={(e) => e.stopPropagation()}>
										<input
											type="checkbox"
											checked={selectedPayments.includes(payment.id)}
											onChange={(e) =>
												onPaymentSelect(payment.id, e.target.checked)
											}
											className="rounded border-border"
										/>
									</td>
									<td className="p-4">
										<div>
											<p className="font-medium text-foreground">
												{payment.tenant_name || "N/A"}
											</p>
											{payment.reference_number && (
												<p className="text-xs text-muted-foreground">
													Ref: {payment.reference_number}
												</p>
											)}
										</div>
									</td>
									<td className="p-4">
										<p className="text-sm text-foreground">
											{payment.property_name || "N/A"}
										</p>
									</td>
									<td className="p-4">
										<p className="font-medium text-foreground">
											{formatCurrency(payment.amount)}
										</p>
									</td>
									<td className="p-4">
										<div>
											<p className="text-sm text-foreground">
												{formatDateTime(payment.due_date)}
											</p>
											{payment.paid_date && (
												<p className="text-xs text-muted-foreground">
													Paid: {formatDateTime(payment.paid_date)}
												</p>
											)}
										</div>
									</td>
									<td className="p-4">
										<span
											className={getStatusBadge(
												payment.status,
												payment.due_date,
											)}
										>
											{isOverdue(payment) ? "Overdue" : payment.status}
										</span>
									</td>
									<td className="p-4" onClick={(e) => e.stopPropagation()}>
										<div className="flex items-center justify-end space-x-1">
											{payment.status !== "paid" && (
												<Button
													variant="ghost"
													size="icon"
													onClick={() => onRecordPayment(payment.id)}
													className="h-8 w-8"
													title="Record Payment"
												>
													<Icon name="CheckCircle" size={16} />
												</Button>
											)}
											<Button
												variant="ghost"
												size="icon"
												onClick={() => onDeletePayment(payment.id)}
												className="h-8 w-8 text-destructive hover:text-destructive"
												title="Delete Payment"
											>
												<Icon name="Trash2" size={16} />
											</Button>
										</div>
									</td>
								</tr>
								{expandedRows.has(payment.id) && (
									<tr>
										<td colSpan={7} className="p-4 bg-muted/20">
											<div className="grid grid-cols-2 gap-4 text-sm">
												<div>
													<p className="text-muted-foreground">
														Payment Method
													</p>
													<p className="font-medium text-foreground capitalize">
														{payment.payment_method?.replace("_", " ") || "N/A"}
													</p>
												</div>
												<div>
													<p className="text-muted-foreground">Notes</p>
													<p className="font-medium text-foreground">
														{payment.notes || "No notes"}
													</p>
												</div>
												{payment.payment_proof_url && (
													<div className="col-span-2">
														<p className="text-muted-foreground mb-2">
															Payment Proof
														</p>
														<a
															href={payment.payment_proof_url}
															target="_blank"
															rel="noopener noreferrer"
															className="text-primary hover:underline"
														>
															View Proof
														</a>
													</div>
												)}
											</div>
										</td>
									</tr>
								)}
							</React.Fragment>
						))}
					</tbody>
				</table>
				</div>
			</div>

			{/* Mobile Card Layout */}
			<div className="lg:hidden">
				{sortedPayments.map((payment) => (
					<div
						key={payment.id}
						className="p-4 border-b border-border last:border-b-0 hover:bg-muted/30 cursor-pointer smooth-transition"
						onClick={() => onPaymentClick(payment)}
					>
						<div className="flex items-start space-x-3">
							<input
								type="checkbox"
								checked={selectedPayments.includes(payment.id)}
								onChange={(e) => onPaymentSelect(payment.id, e.target.checked)}
								onClick={(e) => e.stopPropagation()}
								className="mt-1 rounded border-border"
							/>
							<div className="flex-1 min-w-0">
								<div className="flex items-start justify-between">
									<div className="flex-1 min-w-0">
										<p className="font-medium text-foreground truncate">
											{payment.tenant_name || "N/A"}
										</p>
										<p className="text-sm text-muted-foreground truncate">
											{payment.property_name || "N/A"}
										</p>
									</div>
									<div className="flex items-center space-x-1 ml-2">
										{payment.status !== "paid" && (
											<Button
												variant="ghost"
												size="icon"
												onClick={(e) => {
													e.stopPropagation();
													onRecordPayment(payment.id);
												}}
												className="h-8 w-8"
											>
												<Icon name="CheckCircle" size={16} />
											</Button>
										)}
										<Button
											variant="ghost"
											size="icon"
											onClick={(e) => {
												e.stopPropagation();
												onDeletePayment(payment.id);
											}}
											className="h-8 w-8 text-destructive hover:text-destructive"
										>
											<Icon name="Trash2" size={16} />
										</Button>
									</div>
								</div>
								<div className="mt-2 flex items-center justify-between">
									<div>
										<p className="font-medium text-foreground">
											{formatCurrency(payment.amount)}
										</p>
										<p className="text-xs text-muted-foreground">
											Due: {formatDateTime(payment.due_date)}
										</p>
									</div>
									<span
										className={getStatusBadge(payment.status, payment.due_date)}
									>
										{isOverdue(payment) ? "Overdue" : payment.status}
									</span>
								</div>
							</div>
						</div>
					</div>
				))}
			</div>
		</div>
	);
};

export default PaymentTable;
